import {
  Button,
  CloseButton,
  FileDrop,
  Flex,
  Grid,
  Heading,
  IconImageLine,
  Modal,
  Text,
  TextInput,
  View,
} from "@instructure/ui";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $insertNodes } from "lexical";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { $createImageNode } from "../../nodes/ImageNode";

export const ImagePlugin = ({ disabled = false }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [fileError, setFileError] = useState(false);
  const [fileName, setFileName] = useState("");
  const [src, setSrc] = useState("");
  const [altText, setAltText] = useState("");

  const [editor] = useLexicalComposerContext();

  function handleInsert(src, altText) {
    editor.update(() => {
      const node = $createImageNode({ src, altText });
      $insertNodes([node]);
    });
    setOpen(false);
  }

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="toolbar-item"
        aria-label={t("rte.image.insertImage")}
        title={t("rte.image.insertImage")}
        disabled={disabled}
      >
        <IconImageLine size="x-small" />
      </button>
      <Modal
        open={open}
        label={t("rte.image.insertImage")}
        onExited={() => {
          setFileName("");
          setSrc("");
          setAltText("");
          setFileError(false);
        }}
      >
        <Modal.Header>
          <Flex justifyItems="space-between">
            <Flex.Item>
              <Heading as="h3">{t("rte.image.insertImage")}</Heading>
            </Flex.Item>
            <Flex.Item>
              <CloseButton
                offset="small"
                onClick={() => setOpen(false)}
                screenReaderLabel={t("global.close")}
              />
            </Flex.Item>
          </Flex>
        </Modal.Header>
        <Modal.Body>
          <FileDrop
            data-testid="image-file-drop"
            accept={"image/*"}
            onDropAccepted={([file]) => {
              setFileError(false);
              const reader = new FileReader();
              reader.onload = function () {
                if (typeof reader.result === "string") {
                  const src = reader.result;
                  setSrc(src);
                  setFileName(file.name);
                }
              };
              reader.readAsDataURL(file);
            }}
            onDropRejected={() => setFileError(true)}
            messages={
              fileError
                ? [{ text: t("notifications.invalidFileType"), type: "error" }]
                : []
            }
            renderLabel={
              <View as="div" padding="large large">
                <IconImageLine size="large" />
                <Heading>{t("rte.image.uploadImage")}</Heading>
                <Grid>
                  <Grid.Row hAlign="center">
                    <Grid.Col>
                      <Text color="brand">{t("ilo.dragAndDropLabel")}</Text>
                    </Grid.Col>
                  </Grid.Row>
                  <Grid.Row hAlign="center">
                    {fileName && (
                      <Grid.Col>
                        <Text>{fileName}</Text>
                      </Grid.Col>
                    )}
                  </Grid.Row>
                </Grid>
              </View>
            }
            margin="0 0 small 0"
          />
          <TextInput
            renderLabel={t("rte.image.altText")}
            placeholder={t("rte.image.altTextPlaceholder")}
            value={altText}
            onChange={(e) => setAltText(e.target.value)}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button
            color="primary"
            onClick={() => handleInsert(src, altText)}
            disabled={!src}
          >
            {t("global.submit")}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
